import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {APIRequest, APIRequestResources, CursorResponse} from '../../../../../core';
import {
    ComplianceCertificateFindDTO,
    ComplianceCertificateFindParams,
    ComplianceCertificateDTO,
    ComplianceTypeWithCertificatesDTO,
    CreateComplianceCertificatePayload,
    FSC_COMPLIANCE_TYPE_ID,
    UpdateComplianceCertificatePayload
} from '../interface/compliance-certificate.interface';

@Injectable({
    providedIn: 'root'
})
export class ComplianceCertificateService extends APIRequest {
    constructor(protected override http: HttpClient) {
        super(http, APIRequestResources.ComplianceCertificateService);
    }

    getByType = (complianceTypeId: number = FSC_COMPLIANCE_TYPE_ID) => {
        return this.get<ComplianceTypeWithCertificatesDTO>({
            endpoint: `by-type/${complianceTypeId}`
        }).pipe(
            map((res) => this.normalizeTypeResponse(res.data))
        );
    }

    find = (searchParams: ComplianceCertificateFindParams = {}) => {
        const params: Record<string, string | number> = {
            type_name: searchParams.type_name ?? '',
            type_code: searchParams.type_code ?? '',
            certificate_code: searchParams.certificate_code ?? '',
            certificate_name: searchParams.certificate_name ?? '',
            status: searchParams.status ?? 'A',
            cursor: searchParams.cursor ?? -1,
            limit: searchParams.limit ?? 20
        };

        return this.get<CursorResponse<ComplianceCertificateFindDTO[]>>({
            endpoint: 'find',
            params
        }).pipe(
            map((res) => {
                const payload = res.data;
                const rows = this.parseJsonArray<ComplianceCertificateFindDTO>(payload?.data);
                return {
                    ...payload,
                    data: rows
                } as CursorResponse<ComplianceCertificateFindDTO[]>;
            })
        );
    }

    create = (payload: CreateComplianceCertificatePayload) => {
        return this.post<{ complianceTypeId: number; savedCount: number }>(payload, {});
    }

    update = (id: number, payload: UpdateComplianceCertificatePayload) => {
        return this.patch<{ id: number; complianceTypeId: number }>(payload, {
            endpoint: String(id)
        });
    }

    private normalizeTypeResponse(data: ComplianceTypeWithCertificatesDTO | null | undefined): ComplianceTypeWithCertificatesDTO {
        if (!data) {
            return {
                complianceTypeId: FSC_COMPLIANCE_TYPE_ID,
                typeCode: 'FSC',
                typeName: 'Forest',
                certificates: []
            };
        }

        return {
            ...data,
            certificates: this.parseJsonArray<ComplianceCertificateDTO>(data.certificates)
                .filter((certificate) => (certificate.status ?? 'A') === 'A')
        };
    }

    private parseJsonArray<T>(value: unknown): T[] {
        if (!value) {
            return [];
        }
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
        return Array.isArray(value) ? value : [];
    }
}
